import { gql } from "@apollo/client";

/**
 * Extracts essential fields from timelog data for easy access and reporting
 * @param {object} timelogData - Raw timelog data
 * @returns {object} Object with extracted essential fields
 */
const extractEssentialFields = (timelogData) => {
  if (!timelogData) return {};

  const essential = {};

  // Worker information
  if (timelogData.worker) {
    essential.worker_first_name = timelogData.worker.first_name;
    essential.worker_last_name = timelogData.worker.last_name;
    essential.worker_id = timelogData.worker.unique_access_id || timelogData.worker.worker_id;
    essential.agency_name = timelogData.worker.agency?.name;
  }

  // Line information
  if (timelogData.line) {
    essential.line_number = timelogData.line.number;
    if (timelogData.line.location) {
      essential.location_name = timelogData.line.location.name;
    }
  }

  // Line Job information
  if (timelogData.line_job) {
    essential.line_job_id = timelogData.line_job.id ?? null;
    // job_start may be a full timestamp (e.g. "2026-04-22T14:55:00"); extract date-only part
    const rawJobStart = timelogData.line_job.job_start;
    essential.line_job_date = rawJobStart ? rawJobStart.split('T')[0] : timelogData.date;

    if (timelogData.line_job.customer) {
      essential.customer_name = timelogData.line_job.customer.name;
    }

    if (timelogData.line_job.item) {
      essential.item_number = timelogData.line_job.item.number;
    }
  }

  // line_job_id may also be a top-level field (e.g. from bulk mutation returning)
  if (!essential.line_job_id && timelogData.line_job_id != null) {
    essential.line_job_id = timelogData.line_job_id;
  }

  // Position information
  if (timelogData.position) {
    essential.position_name = timelogData.position.comment;
  }

  // Timelog core fields
  essential.date = timelogData.date;
  essential.start_time = timelogData.start_time;
  essential.stop_time = timelogData.stop_time;
  essential.start_type = timelogData.start_type;
  essential.stop_type = timelogData.stop_type;
  essential.state = timelogData.state;
  essential.shift = timelogData.shift;
  essential.lunch_time = timelogData.lunch_time;
  essential.time = timelogData.time;
  essential.base_rate = timelogData.base_rate;
  essential.markup = timelogData.markup;
  essential.cost = timelogData.cost;
  essential.checkout_reason = timelogData.checkout_reason;
  essential.checkout_comment = timelogData.checkout_comment;

  return essential;
};

/**
 * Cleans and prepares timelog data for audit logging
 * Extracts only essential fields for simplified audit trail
 * @param {object} timelogData - Raw timelog data from state or query
 * @returns {object} Object with only essential fields extracted
 */
const prepareTimelogData = (timelogData) => {
  try {
    if (!timelogData) return null;

    // Extract only essential fields
    return extractEssentialFields(timelogData);
  } catch (error) {
    console.warn("[Audit] Error preparing timelog data:", error);
    return null;
  }
};

/**
 * Extracts only the changed fields between before and after data
 * Performs deep comparison to find differences
 * @param {object} beforeData - Original essential fields
 * @param {object} afterData - Updated essential fields
 * @returns {object} Object containing only the changed fields
 */
const extractChangedFields = (beforeData, afterData) => {
  if (!beforeData || !afterData) return afterData;

  const changes = {};

  // Compare each field in afterData with beforeData
  Object.keys(afterData).forEach((key) => {
    const beforeValue = beforeData[key];
    const afterValue = afterData[key];

    // Check if values are different
    // Handle null/undefined comparison
    if (beforeValue !== afterValue) {
      // For objects/arrays, do a simple JSON comparison
      if (typeof afterValue === 'object' && afterValue !== null) {
        if (JSON.stringify(beforeValue) !== JSON.stringify(afterValue)) {
          changes[key] = afterValue;
        }
      } else {
        changes[key] = afterValue;
      }
    }
  });

  return changes;
};

/**
 * GraphQL mutation to insert timelog audit record
 */
const INSERT_TIMELOG_AUDIT = gql`
  mutation InsertTimelogAudit(
    $timelog_id: Int!
    $user_id: Int!
    $action: String!
    $original_data: jsonb
    $updated_data: jsonb
  ) {
    insert_timelog_audit_one(
      object: {
        timelog_id: $timelog_id
        user_id: $user_id
        action: $action
        original_data: $original_data
        updated_data: $updated_data
      }
    ) {
      id
    }
  }
`;

/**
 * Logs a timelog audit record to the database
 * This function is fail-safe and will never throw errors
 *
 * Data structure by action type:
 * - INSERT: originalData = all essential fields, updatedData = null
 * - UPDATE: originalData = all essential fields (before), updatedData = all essential fields (after)
 * - DELETE: originalData = all essential fields, updatedData = null
 *
 * @param {object} client - Apollo client instance
 * @param {number} timelogId - Timelog ID
 * @param {number} userId - User ID who performed the action
 * @param {string} action - Action type: "INSERT", "UPDATE", or "DELETE"
 * @param {object|null} originalData - Original/before state with essential fields
 * @param {object|null} updatedData - Updated/after state with essential fields (UPDATE only)
 * @returns {Promise<boolean>} True if successful, false otherwise
 */
export const logTimelogAudit = async (
  client,
  timelogId,
  userId,
  action,
  originalData = null,
  updatedData = null
) => {
  try {
    // Validate inputs
    if (!client) {
      console.warn("[Audit] Apollo client not provided");
      return false;
    }

    if (!timelogId || !userId || !action) {
      console.warn("[Audit] Missing required parameters:", {
        timelogId,
        userId,
        action,
      });
      return false;
    }

    // Validate action type
    const validActions = ["INSERT", "UPDATE", "DELETE"];
    if (!validActions.includes(action)) {
      console.warn(`[Audit] Invalid action type: ${action}`);
      return false;
    }

    // Prepare mutation variables
    const variables = {
      timelog_id: timelogId,
      user_id: userId,
      action: action,
      original_data: originalData,
      updated_data: updatedData,
    };

    // Execute mutation
    await client.mutate({
      mutation: INSERT_TIMELOG_AUDIT,
      variables,
    });

    return true;
  } catch (error) {
    // Log error but don't throw - this is fail-safe
    console.warn("[Audit] Failed to log audit record:", error.message || error);
    return false;
  }
};

/**
 * Helper function to log INSERT action
 * Uses the timelog data from mutation result or component state
 * @param {object} client - Apollo client instance
 * @param {number} timelogId - Timelog ID that was created
 * @param {number} userId - User ID who created the timelog
 * @param {object} timelogData - Timelog data from mutation result or state
 */
export const logTimelogCreate = (client, timelogId, userId, timelogData) => {
  try {
    if (!timelogData) {
      console.warn("[Audit] No timelog data provided for INSERT audit");
      return;
    }

    // Prepare and clean the data
    const cleanedData = prepareTimelogData(timelogData);

    // Log the audit record (fire and forget - non-blocking)
    logTimelogAudit(client, timelogId, userId, "INSERT", cleanedData, null).catch(
      (err) => console.warn("[Audit] Insert audit failed:", err)
    );
  } catch (error) {
    console.warn("[Audit] Error in logTimelogCreate:", error);
  }
};

/**
 * Helper function to log UPDATE action
 * Uses before/after data from component state
 * For UPDATE actions:
 * - original_data: Contains ALL essential fields from the before state
 * - updated_data: Contains ONLY the fields that were changed
 * @param {object} client - Apollo client instance
 * @param {number} timelogId - Timelog ID that was updated
 * @param {number} userId - User ID who updated the timelog
 * @param {object} beforeData - Timelog state before update (from component state)
 * @param {object} afterData - Timelog state after update (from mutation result or state)
 */
export const logTimelogUpdate = (
  client,
  timelogId,
  userId,
  beforeData,
  afterData
) => {
  try {
    if (!beforeData || !afterData) {
      console.warn("[Audit] Missing before/after data for UPDATE audit");
      return;
    }

    // Prepare and clean the data
    const cleanedBefore = prepareTimelogData(beforeData);
    const cleanedAfter = prepareTimelogData(afterData);

    // Extract only the changed fields for updated_data
    const changedFields = extractChangedFields(cleanedBefore, cleanedAfter);

    // Log the audit record:
    // - original_data: ALL essential fields from the BEFORE state
    // - updated_data: ONLY the fields that changed (with their new/after values)
    logTimelogAudit(
      client,
      timelogId,
      userId,
      "UPDATE",
      cleanedBefore,
      changedFields
    ).catch((err) => console.warn("[Audit] Update audit failed:", err));
  } catch (error) {
    console.warn("[Audit] Error in logTimelogUpdate:", error);
  }
};

/**
 * Helper function to log DELETE action
 * Logs the original timelog data so we know what was deleted
 * @param {object} client - Apollo client instance
 * @param {number} timelogId - Timelog ID that was deleted
 * @param {number} userId - User ID who deleted the timelog
 * @param {object} timelogData - Timelog data before deletion (from component state)
 */
export const logTimelogDelete = (client, timelogId, userId, timelogData) => {
  try {
    // For DELETE, save the original data so we know what was deleted
    const cleanedData = timelogData ? prepareTimelogData(timelogData) : null;

    // Fire and forget - non-blocking
    logTimelogAudit(client, timelogId, userId, "DELETE", cleanedData, null).catch(
      (err) => console.warn("[Audit] Delete audit failed:", err)
    );
  } catch (error) {
    console.warn("[Audit] Error in logTimelogDelete:", error);
  }
};
